import { Avatar, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { ReactElement, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../redux/store";
import { getPostReportList } from "../redux/Reducers/postReportsReducer";
import { useLocation, useNavigate } from "react-router-dom";
import { colors } from "../common/colors";
import GCBackButton from "../components/GreenCity/GCBackButton";
import LoadingModal from "../components/LoadingModal";
import MessageModal from "../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "./ErrorMessageModal";
import appServices from "../services/app-services/appServices";
import Separator from "./Separator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { convertUserImage } from "../util/utils";
import { T } from "react-translator-component";

interface PostCreator {
  image: any;
  name: string;
}

export default function PostReport(): ReactElement {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const postId: number = location.state.postId;
  const postCreator: PostCreator = location.state.postCreator;
  const { user } = useSelector((state: any) => state.userAuth);
  const [loading, setLoading] = useState<boolean>(false);
  const [otherReason, setOtherReason] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false);
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState<boolean>(false);
  const [isResponseSuccess, setIsResponseSuccess] = useState<boolean>(false);

  const defaultReportReason = "投稿内容に不適切な内容が含まれているため。"

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await appServices.createPostReport(postId, otherReason ? reason : defaultReportReason);
      await dispatch(getPostReportList())
      setMessage(`${T('reported')}`);
      setOpenMessageModal(true)
      setIsResponseSuccess(true)
    } catch (error: any) {
      console.error(error);
      setMessage(error?.response?.data?.message ?? T("api_unknown_error"));
      setOpenErrorMessageModal(true)
    } finally {
      setLoading(false);
    }
  };

  const closeMessageModal = () => {
    if (isResponseSuccess) {
      navigate(-1)
    } else {
      setMessage("");
      setOpenMessageModal(false)
      setOpenErrorMessageModal(false)
    }
  }

  return (
    <>
      <div style={styles.container}>
        <LoadingModal visible={loading} />
        <GCBackButton onClick={() => navigate(-1)} />

        <div style={styles.titleSection}>
          <div style={styles.personalInfo}>
            <Avatar
              src={convertUserImage(user.image)}
              sx={styles.userImage}
            />
            <Typography style={styles.title}>{T("execute_report")}</Typography>
          </div>
        </div>

        <Separator color={colors.farm_gray100} />

        <div style={styles.reportContainer}>
          <div style={styles.personalInfo}>
            <Avatar
              src={postCreator.image}
              sx={styles.personalImage}
            />
            <Typography style={styles.creatorName}>{postCreator.name}</Typography>
          </div>

          <Typography style={styles.howReason}>
            {T("tell_reason_why_this_user_is_reported")}
          </Typography>

          <Separator color={colors.farm_green500} sx={{ height: "1.5px" }} />

          <div style={styles.formContainer}>
            <div>
              <FormControlLabel
                control={
                  <Checkbox checked={!otherReason} sx={styles.checkBox} onChange={() => setOtherReason(false)} />
                }
                color="error"
                sx={styles.formCheck}
                label={
                  <Typography style={styles.checkLabel}>
                    {T("report_default_reason")}
                  </Typography>
                }
              />
            </div>
            <div>
              <FormControlLabel
                control={
                  <Checkbox checked={otherReason} onChange={() => setOtherReason(true)} />
                }
                color="error"
                sx={styles.formCheck}
                label={
                  <Typography style={styles.checkLabel}>
                    {T("other")}
                  </Typography>
                }
              />
            </div>

            <TextField
              style={styles.inputFieldTxt}
              multiline
              rows={3}
              value={reason}
              disabled={!otherReason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={`${T("report_placeholder")}`}
            />
          </div>

          <div style={styles.submitContainer}>
            <Button
              onClick={() => handleSubmit()}
              style={styles.submit}
              size={"large"}
              variant="contained"
              endIcon={<FontAwesomeIcon icon={faChevronRight} />}
            >
              <span style={styles.buttonText}>{T("execute_report")}</span>
            </Button>
          </div>
        </div>
      </div>

      <MessageModal
        open={openMessageModal}
        message={message}
        onContinue={() => closeMessageModal()}
        onClose={() => closeMessageModal()}
      />

      <ErrorMessageModal
        open={openErrorMessageModal}
        messages={[message]}
        onClose={() => closeMessageModal()}
      />
    </>
  );
}

const styles = {
  container: {
    paddingTop: 100,
  },

  titleSection: {
    marginLeft: 10,
    marginBottom: 16,
  },

  personalInfo: {
    display: "flex",
    alignItems: "center",
    marginTop: 20,
  },

  userImage: {
    height: 40,
    width: 40,
    marginRight: "10px",
  },

  reportContainer: {
    margin: "0 20px"
  },

  personalImage: {
    height: 50,
    width: 50,
    marginRight: "10px",
  },

  title: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: "50px",
  },

  creatorName: {
    fontSize: 18,
    lineHeight: "50px",
  },

  howReason: {
    marginTop: 25,
    marginBottom: 15,
    fontSize: 15,
  },

  formContainer: {
    margin: "0 10px",
  },

  formCheck: {
    marginTop: "20px",
    marginLeft: "-10px",
    marginRight: 0,
  },

  checkBox: {
    padding: "0 9px 0",
  },

  checkLabel: {
    fontSize: 16,
    width: "100%"
  },

  inputFieldTxt: {
    marginTop: "20px",
    fontSize: 16,
    width: "100%"
  },

  submitContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginTop: "35px",
    display: "flex",
  },

  submit: {
    alignSelf: "center",
    borderRadius: 20,
    backgroundColor: colors.farm_blue200,
    width: "80%"
  },

  buttonText: {
    minWidth: 200
  }
}
